/**
 * Compute the Sørensen–Dice coefficient between two strings.
 * Returns a value between 0 (no similarity) and 1 (identical bigram sets).
 */
export function diceCoefficient(a: string, b: string): number {
  const left = a.toLowerCase()
  const right = b.toLowerCase()

  if (left === right) return 1
  if (left.length < 2 || right.length < 2) return 0

  const bigramsA = new Map<string, number>()
  for (let i = 0; i < left.length - 1; i++) {
    const bigram = left.substring(i, i + 2)
    bigramsA.set(bigram, (bigramsA.get(bigram) ?? 0) + 1)
  }

  let intersectionCount = 0
  for (let i = 0; i < right.length - 1; i++) {
    const bigram = right.substring(i, i + 2)
    const count = bigramsA.get(bigram)
    if (count && count > 0) {
      intersectionCount++
      bigramsA.set(bigram, count - 1)
    }
  }

  return (2 * intersectionCount) / (left.length - 1 + (right.length - 1))
}

const FUZZY_THRESHOLD = 0.6

/** Scale threshold down for very short queries where dice naturally produces lower scores. */
function effectiveThreshold(queryLength: number): number {
  if (queryLength <= 3) return 0.4
  if (queryLength <= 5) return 0.5
  return FUZZY_THRESHOLD
}

/**
 * Returns true if `candidate` is a fuzzy match for `query`.
 *
 * Strategy:
 * 1. Substring containment (always passes)
 * 2. Per-word dice comparison
 * 3. Sliding-window dice: compare query against each window of candidate
 *    of the same length — handles short queries against long words
 */
export function isFuzzyMatch(query: string, candidate: string): boolean {
  const q = query.toLowerCase()
  const c = candidate.toLowerCase()

  // Short-circuit: substring match always passes
  if (c.includes(q)) return true

  // For very short queries (1 char), only substring match makes sense
  if (q.length < 2) return false

  const threshold = effectiveThreshold(q.length)

  // Per-word comparison
  const candidateWords = c.split(/[\s\-_/]+/)
  for (const word of candidateWords) {
    if (diceCoefficient(q, word) >= threshold) return true

    // Sliding window: compare query against each substring of word
    // with length = query length. Catches near-prefix typos like "pak" → "pac(kages)"
    if (word.length > q.length) {
      for (let i = 0; i <= word.length - q.length; i++) {
        const window = word.substring(i, i + q.length)
        if (diceCoefficient(q, window) >= threshold) return true
      }
    }
  }

  return false
}
